import SelectField from '@pelckmans/elementis/components/select-field';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import { Subject } from '../../../../api/MyGroupsApi';
import { fetchTeacherProfile, SubjectYear } from '../../../../../../api/my-account';

interface Props {
  editable: boolean;
  subjects: Subject[];
  isLoadingSubjects: boolean;
  handleChange: (subjects: Subject[]) => void;
  handleBlur?: () => void;
  formSubjects: Subject[];
  errorText?: string;
}

function GroupDetailSubjectsSelector({
  subjects,
  isLoadingSubjects,
  editable,
  handleChange,
  handleBlur,
  formSubjects,
  errorText,
}: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, onSearchValueChange] = useState('');
  const [subjectYears, setSubjectYears] = useState<SubjectYear[]>([]);
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    const getSubjectYears = async () => {
      setIsLoading(true);
      const { subjectYears: fetchedSubjectYears } = await fetchTeacherProfile();

      setSubjectYears(
        fetchedSubjectYears.sort((a, b) => {
          return a.subject.name
            .toLowerCase()
            .localeCompare(b.subject.name.toLowerCase(), language, { sensitivity: 'base' });
        }),
      );
      setIsLoading(false);
    };

    getSubjectYears().catch(handleError);
  }, [handleError, language]);

  const subjectsJsx = useMemo(() => {
    if (subjects.length === 0 && subjectYears.length === 0) {
      return [];
    }

    const filteredSubjectYears = subjectYears.filter(x =>
      x.subject.name.toLowerCase().includes(searchValue.toLowerCase()),
    );

    if (subjectYears.length === 0 || filteredSubjectYears.length === 0) {
      return subjects
        .filter(x => x.name.toLowerCase().includes(searchValue.toLowerCase()))
        .map(subject => <SelectField.Item key={subject.id} value={subject} text={subject.name} />);
    }

    return (
      <>
        {filteredSubjectYears.length > 0 && (
          <SelectField.Group
            title={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.LISTS.MY_SUBJECTS.TITLE')}
          >
            {filteredSubjectYears.map(({ subject }) => (
              <SelectField.Item key={subject.id} value={subject} text={subject.name} />
            ))}
          </SelectField.Group>
        )}
        {subjects.length > 0 && (
          <SelectField.Group
            title={t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.LISTS.SUBJECTS.TITLE')}
          >
            {subjects
              .filter(x => x.name.toLowerCase().includes(searchValue.toLowerCase()))
              .map(subject => (
                <SelectField.Item key={subject.id} value={subject} text={subject.name} />
              ))}
          </SelectField.Group>
        )}
      </>
    );
  }, [searchValue, subjects, subjectYears, t]);

  return (
    <SelectField
      id="subjects"
      disabled={!editable}
      required
      multiple
      searchable
      onChange={handleChange}
      onBlur={handleBlur}
      value={formSubjects}
      renderValueContent={option => option.name}
      matchOn="id"
      isLoading={isLoading || isLoadingSubjects}
      error={!!errorText}
      errorText={errorText}
      labelText={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.LABEL')}
      placeholder={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.PLACEHOLDER')}
      dropdownMenuProps={{
        ariaLabel: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.A11Y_LABEL'),
      }}
      selectedValueLabel={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.SELECTED_LABEL')}
      searchProps={{
        searchValue,
        onSearchValueChange,
        inputProps: {
          ariaLabel: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SUBJECT.SEARCH.A11Y_LABEL'),
        },
      }}
    >
      {subjectsJsx}
    </SelectField>
  );
}

export default GroupDetailSubjectsSelector;
